import { useEffect, useRef } from 'react';
type Particle = {
  x: number;
  y: number;
  size: number;
  opacity: number;
  tone: string;
};

const PURPLES = ['#145A35', '#145A35', '#1F7A49', '#1F7A49', '#45A66B', '#45A66B', '#77C892', '#D2C09A'];
const ACCENTS = ['#45A66B', '#77C892', '#D2C09A', '#FFFFFF'];

const buildLivingParticles = (count: number): Particle[] =>
  Array.from({ length: count }, (_, index) => {
    const angle = (index * 137.508 * Math.PI) / 180 + Math.sin(index * 0.61) * 0.12;
    const ring = Math.sqrt((index + 1) / count);
    const wave = Math.sin(index * 1.91) * 0.025 + Math.cos(index * 0.43) * 0.018;
    const radius = Math.max(0.025, Math.min(0.47, ring * 0.445 + wave));
    const x = 0.5 + Math.cos(angle) * radius;
    const y = 0.5 + Math.sin(angle) * radius * (0.88 + (index % 7) * 0.012);
    const size = 1.15 + ((index * 11) % 13) * 0.18;
    const opacity = 0.48 + ((index * 17) % 11) * 0.045;
    const accent = index % 23 === 0 || index % 31 === 0;
    const tone = accent ? ACCENTS[index % ACCENTS.length] : PURPLES[(index * 5 + index % 3) % PURPLES.length];
    return { x, y, size, opacity: Math.min(1, opacity), tone };
  });


const PARTICLES = buildLivingParticles(168);
/** SALRI's fixed Praamis particle geometry and green palette, adapted to DOM rendering. */
export default function PraamisOrb({ size = 68 }: {size?:number}) {
  const wrap = useRef<HTMLSpanElement>(null);
  const layer = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    let animations: Animation[] = [];
    function start() {
      animations.forEach(animation => animation.cancel()); animations = [];
      if(reduced.matches || !wrap.current || !layer.current) return;
      animations = [
        wrap.current.animate([{transform:'translateY(-4px) rotate(-1.2deg)'},{transform:'translateY(5px) rotate(1.2deg)'}],{duration:1600,direction:'alternate',iterations:Infinity,easing:'ease-in-out'}),
        layer.current.animate([{transform:'rotate(360deg)'},{transform:'rotate(0deg)'}],{duration:9000,iterations:Infinity}),
        layer.current.animate([{opacity:.74},{opacity:.96},{opacity:.68}],{duration:1640,iterations:Infinity}),
      ];
    }
    start(); reduced.addEventListener('change',start);
    return () => {animations.forEach(animation => animation.cancel());reduced.removeEventListener('change',start);};
  },[]);
  return <span ref={wrap} className="praamis-salri-orb" style={{width:size,height:size}} aria-hidden="true">
    <span className="praamis-orb-pulse"><span ref={layer} className="praamis-orb-particles">
      {PARTICLES.map((p,index) => {const dot=Math.max(1.1,p.size*(size/160));return <span key={index} style={{position:'absolute',left:p.x*size-dot/2,top:p.y*size-dot/2,width:dot,height:dot,borderRadius:dot,backgroundColor:p.tone,opacity:p.opacity,boxShadow:'0 0 '+Math.max(2,dot*2.1)+'px '+p.tone}} />;})}
    </span>
    {[
      {left:size*.08,top:size*.18,width:size*.022,height:size*.022,backgroundColor:'#D2C09A'},
      {right:size*.07,bottom:size*.16,width:size*.027,height:size*.027,backgroundColor:'#FFFFFF'},
      {left:size*.54,top:size*.035,width:size*.018,height:size*.018,backgroundColor:'#77C892'},
      {left:size*.22,bottom:size*.06,width:size*.017,height:size*.017,backgroundColor:'#45A66B'},
    ].map((style,index)=><span className="praamis-orb-spark" key={index} style={style}/>)}
    </span>
  </span>;
}

